/**
 * AuthenticatedClient - Production-grade HTTP client with automatic token refresh
 *
 * Responsibilities:
 * - HTTP requests with automatic token injection
 * - Transparent token refresh on expiration
 * - Standardized error handling for auth failures
 * - Request/response logging for debugging
 */

import { TokenManager } from './tokenManager';
import { supabase } from './supabase';

interface RequestOptions extends RequestInit {
  skipAuth?: boolean;
}

interface AuthenticatedResponse<T = unknown> {
  data: T | null;
  error: string | null;
  status: number;
}

export class AuthenticatedClient {
  private static readonly baseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL;
  private static readonly apiKey = process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY;

  /**
   * Make an authenticated request with automatic token refresh
   */
  static async request<T = unknown>(
    endpoint: string, 
    options: RequestOptions = {}
  ): Promise<AuthenticatedResponse<T>> {
    const { skipAuth = false, ...requestOptions } = options;

    if (!this.baseUrl || !this.apiKey) {
      console.error('🚨 AuthenticatedClient: Missing Supabase environment variables');
      return {
        data: null,
        error: 'Configuration error: Missing Supabase credentials',
        status: 500
      };
    }

    // Build full URL
    const url = endpoint.startsWith('http') ? endpoint : `${this.baseUrl}${endpoint}`;

    // Prepare headers
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Accept': 'application/json',  // Required to prevent 406 errors
      'apikey': this.apiKey,
      ...requestOptions.headers as Record<string, string>
    };

    // Add authentication if not skipped
    if (!skipAuth) {
      let token = await TokenManager.getValidAccessToken();

      // Fallback: If TokenManager has no token, try to get it from Supabase session
      if (!token) {
        console.log('🔄 AuthenticatedClient: No token in TokenManager, checking Supabase session...');
        const { data: sessionData } = await supabase.auth.getSession();

        if (sessionData.session) {
          console.log('✅ AuthenticatedClient: Found Supabase session, storing in TokenManager');
          // Store the session in TokenManager for future requests
          TokenManager.storeTokens({
            access_token: sessionData.session.access_token,
            refresh_token: sessionData.session.refresh_token,
            expires_in: sessionData.session.expires_in || 3600,
            user: {
              id: sessionData.session.user.id,
              email: sessionData.session.user.email
            }
          });
          token = sessionData.session.access_token;
        } else {
          console.warn('⚠️ AuthenticatedClient: No valid token available for authenticated request');
          return {
            data: null,
            error: 'Authentication required',
            status: 401
          };
        }
      }

      headers['Authorization'] = `Bearer ${token}`;
    }

    // Log request for debugging
    console.log(`📡 AuthenticatedClient: ${requestOptions.method || 'GET'} ${endpoint}`, {
      authenticated: !skipAuth,
      hasBody: !!requestOptions.body
    });

    try {
      const response = await fetch(url, {
        ...requestOptions,
        headers
      });

      const responseData = await this.parseResponse<T>(response);

      // Log response for debugging
      console.log(`📡 AuthenticatedClient: Response ${response.status}`, {
        endpoint,
        success: response.ok,
        hasData: !!responseData,
        data: !response.ok ? responseData : undefined  // Log error responses
      });

      return {
        data: response.ok ? responseData : null,
        error: response.ok ? null : this.extractErrorMessage(responseData),
        status: response.status
      };

    } catch (error) {
      console.error('🚨 AuthenticatedClient: Network error:', error);
      return {
        data: null,
        error: `Network error: ${error instanceof Error ? error.message : 'Unknown error'}`,
        status: 0
      };
    }
  }

  /**
   * Convenience method for GET requests
   */
  static async get<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<AuthenticatedResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'GET' });
  }

  /**
   * Convenience method for POST requests
   */
  static async post<T = unknown>(
    endpoint: string, 
    data?: unknown, 
    options: RequestOptions = {}
  ): Promise<AuthenticatedResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Convenience method for PUT requests
   */
  static async put<T = unknown>(
    endpoint: string, 
    data?: unknown, 
    options: RequestOptions = {}
  ): Promise<AuthenticatedResponse<T>> {
    return this.request<T>(endpoint, {
      ...options,
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined
    });
  }

  /**
   * Convenience method for DELETE requests
   */
  static async delete<T = unknown>(endpoint: string, options: RequestOptions = {}): Promise<AuthenticatedResponse<T>> {
    return this.request<T>(endpoint, { ...options, method: 'DELETE' });
  }

  /**
   * Parse response body safely
   */
  private static async parseResponse<T>(response: Response): Promise<T | null> {
    const contentType = response.headers.get('content-type');
    
    if (!contentType?.includes('application/json')) {
      const text = await response.text();
      return text as unknown as T;
    }

    try {
      return await response.json();
    } catch (error) {
      console.error('🚨 AuthenticatedClient: Failed to parse JSON response:', error);
      return null;
    }
  }

  /**
   * Extract error message from response data
   */
  private static extractErrorMessage(responseData: unknown): string {
    if (typeof responseData === 'string') return responseData;
    if (typeof responseData === 'object' && responseData !== null) {
      const data = responseData as Record<string, unknown>;
      if (data.error && typeof data.error === 'string') return data.error;
      if (data.message && typeof data.message === 'string') return data.message;
      if (data.error_description && typeof data.error_description === 'string') return data.error_description;
    }
    return 'Unknown error occurred';
  }

}

/**
 * Specialized methods for Supabase REST API
 */
export class SupabaseClient {
  /**
   * Query Supabase table with automatic authentication
   */
  static async select<T = unknown>(
    table: string, 
    query: string = '*',
    filters: Record<string, unknown> = {}
  ): Promise<AuthenticatedResponse<T[]>> {
    const params = new URLSearchParams();
    params.append('select', query);
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, `eq.${value}`);
      }
    });

    const endpoint = `/rest/v1/${table}?${params.toString()}`;
    return AuthenticatedClient.get<T[]>(endpoint);
  }

  /**
   * Insert into Supabase table
   */
  static async insert<T = unknown>(
    table: string, 
    data: unknown | unknown[]
  ): Promise<AuthenticatedResponse<T>> {
    const endpoint = `/rest/v1/${table}`;
    return AuthenticatedClient.post<T>(endpoint, data, {
      headers: { 'Prefer': 'return=representation' }
    });
  }

  /**
   * Update Supabase table records
   */
  static async update<T = unknown>(
    table: string, 
    data: unknown,
    filters: Record<string, unknown>
  ): Promise<AuthenticatedResponse<T>> {
    const params = new URLSearchParams();
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, `eq.${value}`);
      }
    });

    const endpoint = `/rest/v1/${table}?${params.toString()}`;
    return AuthenticatedClient.put<T>(endpoint, data, {
      headers: { 'Prefer': 'return=representation' }
    });
  }

  /**
   * Delete from Supabase table
   */
  static async deleteFn<T = unknown>(
    table: string,
    filters: Record<string, unknown>
  ): Promise<AuthenticatedResponse<T>> {
    const params = new URLSearchParams();
    
    // Add filters
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        params.append(key, `eq.${value}`);
      }
    });

    const endpoint = `/rest/v1/${table}?${params.toString()}`;
    return AuthenticatedClient.delete<T>(endpoint);
  }
}

/**
 * Auth-specific requests (these skip automatic token injection)
 */
export class AuthClient {
  /**
   * Sign in with email/password
   */
  static async signIn(email: string, password: string) {
    return AuthenticatedClient.post('/auth/v1/token?grant_type=password', {
      email,
      password,
      gotrue_meta_security: {}
    }, { skipAuth: true });
  }

  /**
   * Sign up with email/password
   */
  static async signUp(email: string, password: string, metadata?: unknown) {
    return AuthenticatedClient.post('/auth/v1/signup', {
      email,
      password,
      data: metadata || {}
    }, { skipAuth: true });
  }

  /**
   * Get current user info
   */
  static async getUser() {
    return AuthenticatedClient.get('/auth/v1/user');
  }

  /**
   * Sign out (revoke tokens)
   */
  static async signOut() {
    return AuthenticatedClient.post('/auth/v1/logout', {});
  }
}

// Export namespaces for backward compatibility
export const AuthenticatedClientCompat = {
  Supabase: SupabaseClient,
  Auth: AuthClient
};